import { FC } from 'react';
import { useRecoilValue } from 'recoil';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

import * as K from 'common/constants';
import { abbreviateNumber, AbbreviateNumberFormat, formatMoney } from 'common/util/number';
import { parseDateTime } from 'common/util/time';
import { buildUrl } from 'common/util/location';
import { formatBillingDate } from 'components/billingAndUsage/utils';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { HELP_ROUTE } from 'components/help/constants';

import Card from 'components/common/Card/Card';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import Link from 'components/common/Link/Link';

import { UrlParams } from 'common/types';
import { HindsightDataResolver } from 'common/graphqlTypes';

import 'components/billingAndUsage/BillingAndUsagePage.scss';
import './Hindsight.scss';

const DEFAULT_STORAGE_PQ = `tag='hindsightStorageBytes'
| let dollarsPerTBPerMonth = 22
| let storageTB = value/1000/1024/1024/1024
| columns date.timestamp = timebucket(timestamp, '1d'), accountEmail, 'TB stored' = format('%.3f', storageTB), Cost = format('$ %1.4f', storageTB * dollarsPerTBPerMonth / 30)`;
const DEFAULT_SEARCH_FILTER = "tag = 'queryOutcome' ltrCoverBytes > 0 billable = true";
const DEFAULT_SEARCH_PQ = `tag = 'queryOutcome' ltrCoverBytes > 0 billable = true
| let dollarsPerTB = 0.50
| let coverTB = ltrCoverBytes/1000/1024/1024/1024
| columns timestamp, userEmail, 'TB queried'=format('%.2f', coverTB), Cost = format('$ %1.2f', coverTB * dollarsPerTB), queryPurpose, startTime, endTime, filter`;

const BILLING_START_DATE = '2022-01-01';
interface HindsightProps {
  startDate: string;
  endDate: string;
  hindsightData?: HindsightDataResolver;
}

/**
 * Show a section on the billing page providing a summary of Hindsight usage and cost, with links to searches that
 * allow the user to dig deeper.
 *
 * @prop startDate        first date covered by Hindsight data
 * @prop endDate          last date covered by Hindsight data
 * @prop hindsightData    storage and search data
 */
export const Hindsight: FC<HindsightProps> = ({ startDate, endDate, hindsightData }: HindsightProps) => {
  const timezone = useRecoilValue(timezoneState);

  if (!hindsightData) {
    return null;
  }

  // any costs before billing start date are not actually billed
  const parsedBillingStart = parseDateTime(BILLING_START_DATE, null, null, timezone);
  const billingStartMs = parsedBillingStart?.milliseconds || 0;

  const formattedStartDate = formatBillingDate(startDate, timezone);
  const formattedEndDate = formatBillingDate(endDate, timezone);
  const parsedEndDate = parseDateTime(endDate, null, null, timezone);
  const endDateMs = parsedEndDate?.milliseconds || 0;

  let billingStartText = '';
  const billingOff = billingStartMs > endDateMs;
  const billingDate = formatBillingDate(billingStartMs, timezone);
  if (billingOff) {
    billingStartText = `(billing starts on ${billingDate})`;
  }

  const hindsightHeader = (
    <div className="CurrentBillingSettings__SplitHeader">
      <div>
        <span>Hindsight Usage {billingStartText}</span>
        <Link className="link" openInNewTab href={HELP_ROUTE.HELP_HINDSIGHT}>
          <ElementWithTooltip tooltip="Help">
            <HelpOutlineOutlinedIcon className="CurrentBillingSettings__Title--Icon" />
          </ElementWithTooltip>
        </Link>
      </div>
      <div className="CurrentBillingSettings__UsageGraph__Header__Range">{`${formattedStartDate} - ${formattedEndDate}`}</div>
    </div>
  );

  const storageAverage = abbreviateNumber(
    hindsightData.storageAverageVolume,
    AbbreviateNumberFormat.AUTOBYTE,
    undefined,
    true,
  );
  const storageAverageText = storageAverage === 'NaN' ? '0' : storageAverage;
  const storageSummary = `${storageAverageText}/day (average)`;
  const storageCost = hindsightData.storageTotalCost;

  const storageLink = buildUrl(K.ROUTE.POWER_QUERY, {
    [K.URL_PARAM.FILTER]: hindsightData.storageCostsPowerQuery || DEFAULT_STORAGE_PQ,
    [K.URL_PARAM.START_TIME]: startDate,
    [K.URL_PARAM.END_TIME]: endDate,
  } as UrlParams);

  let searchSummary = '';
  const searchCost = hindsightData.searchTotalCost;
  if (hindsightData.searchCount > 0) {
    const searchVolumeTotal = abbreviateNumber(
      hindsightData.searchTotalVolume,
      AbbreviateNumberFormat.AUTOBYTE,
      undefined,
      true,
    );
    searchSummary = `${hindsightData.searchCount} queries, ${searchVolumeTotal} total`;
  }

  const costClass = billingOff ? 'Hindsight__Cost--Muted' : 'Hindsight__Cost';

  const searchLink = buildUrl(K.ROUTE.EVENTS, {
    [K.URL_PARAM.FILTER]: hindsightData.searchDetailsQuery || DEFAULT_SEARCH_FILTER,
    [K.URL_PARAM.START_TIME]: startDate,
    [K.URL_PARAM.END_TIME]: endDate,
  } as UrlParams);
  const pqLink = buildUrl(K.ROUTE.POWER_QUERY, {
    [K.URL_PARAM.FILTER]: hindsightData.searchCostsPowerQuery || DEFAULT_SEARCH_PQ,
    [K.URL_PARAM.START_TIME]: startDate,
    [K.URL_PARAM.END_TIME]: endDate,
  } as UrlParams);

  return (
    <Card
      header={hindsightHeader}
      className="CurrentBillingSettings__Card"
      headerClass="CurrentBillingSettings__Header"
    >
      <div>
        <div className="Hindsight__Item">
          <div>
            <div className="Hindsight__Name">Storage</div>
            <div className="Hindsight__Summary">{storageSummary}</div>
            <div>
              <Link href={storageLink} className="Hindsight__Link" openInNewTab>
                View Storage Costs
              </Link>
            </div>
          </div>
          {storageCost > 0 && <div className={costClass}>{formatMoney(storageCost)}</div>}
        </div>
        <div className="Hindsight__Item">
          <div>
            <div className="Hindsight__Name">Search on demand</div>
            <div className="Hindsight__Summary">{searchSummary}</div>
            <div>
              <Link href={searchLink} className="Hindsight__Link" openInNewTab>
                View Searches
              </Link>
              <Link href={pqLink} className="Hindsight__Link" openInNewTab>
                View Costs
              </Link>
            </div>
          </div>
          {searchCost > 0 && <div className={costClass}>{formatMoney(searchCost)}</div>}
        </div>
        <div className="Hindsight__Item Hindsight__Total">
          <div>
            <div className="Hindsight__Name">Total</div>
            <div />
          </div>
          <div className={costClass}>{formatMoney(storageCost + searchCost)}</div>
        </div>
      </div>
    </Card>
  );
};
